import React, { useState, useMemo } from 'react';
import api from '../services/api';

// Maps permission app_name → display label
const APP_LABEL_MAP = {
  portfolio_blender: 'Portfolio Blender',
  fund_screener: 'Fund Screener',
  retirement_calculator: 'Retirement Calculator',
  portfolio_constructor: 'Portfolio Constructor',
  risk_model: 'Risk Model',
  research_reports: 'Reports',
  backtest_dashboard: 'Tactical',
  portfolio_dashboard: 'Dashboard',
  cpd: 'CPD',
  resources: 'Resources',
  fact_sheets: 'Fact Sheets',
  portfolio_reports: 'Portfolio Reports',
  portfolio_viewer: 'Portfolio Viewer',
  announcements: 'Announcements',
  market_data: 'Market & Economic Data',
  portfolio_comparison: 'Portfolio Comparison',
};

const FeedbackModal = ({ show, onClose, user }) => {
  const [type, setType] = useState('feedback');
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [appName, setAppName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const appOptions = useMemo(() => {
    const allowed = user?.allowed_apps || [];
    const isDrummond = user?.is_drummond_user;
    const keys = isDrummond ? Object.keys(APP_LABEL_MAP) : allowed;
    const options = keys
      .map(k => APP_LABEL_MAP[k])
      .filter(Boolean)
      .sort();
    options.push('Other');
    return options;
  }, [user]);

  if (!show) return null;

  const resetForm = () => {
    setType('feedback');
    setSubject('');
    setDescription('');
    setAppName('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!subject.trim()) {
      setError('Please add a subject.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/feedback', {
        type,
        subject: subject.trim(),
        description: description.trim(),
        app_name: appName || null,
      });
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal-backdrop-custom"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '560px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px 28px 16px',
            borderBottom: '2px solid var(--secondary-color, #f0a500)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h4
            style={{
              margin: 0,
              color: 'var(--primary-color, #072040)',
              fontWeight: 700,
              fontSize: '1.3rem',
            }}
          >
            Submit {type === 'bug' ? 'Bug Report' : 'Feedback'}
          </h4>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--gray-color, #6c757d)',
              padding: '0 4px',
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ padding: '24px 28px' }}>
          {success ? (
            <div
              style={{
                textAlign: 'center',
                padding: '24px 0',
                color: 'var(--success-color, #91CF9B)',
                fontSize: '1.1rem',
                fontWeight: 600,
              }}
            >
              <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '1.5rem' }}></i>
              Thank you! Your submission has been received.
            </div>
          ) : (
            <>
              {/* Type toggle */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontWeight: 600,
                    color: 'var(--primary-color, #072040)',
                    fontSize: '0.9rem',
                  }}
                >
                  Type
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['feedback', 'bug'].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t)}
                      style={{
                        flex: 1,
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: `2px solid ${type === t ? 'var(--info-color, #599BE3)' : '#dee2e6'}`,
                        backgroundColor: type === t ? 'var(--info-color, #599BE3)' : '#fff',
                        color: type === t ? '#fff' : 'var(--dark-color, #212529)',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {t === 'bug' ? 'Bug Report' : 'Feedback'}
                    </button>
                  ))}
                </div>
              </div>

              {/* App dropdown */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontWeight: 600,
                    color: 'var(--primary-color, #072040)',
                    fontSize: '0.9rem',
                  }}
                >
                  Related App <span style={{ fontWeight: 400, color: 'var(--gray-color)' }}>(optional)</span>
                </label>
                <select
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--input-border, #599BE3)',
                    fontSize: '0.9rem',
                    color: 'var(--dark-color, #212529)',
                  }}
                >
                  <option value="">— Select an app —</option>
                  {appOptions.map((app) => (
                    <option key={app} value={app}>
                      {app}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontWeight: 600,
                    color: 'var(--primary-color, #072040)',
                    fontSize: '0.9rem',
                  }}
                >
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  maxLength={200}
                  placeholder="Brief summary"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--input-border, #599BE3)',
                    fontSize: '0.9rem',
                  }}
                />
              </div>

              {/* Description */}
              <div style={{ marginBottom: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '6px',
                    fontWeight: 600,
                    color: 'var(--primary-color, #072040)',
                    fontSize: '0.9rem',
                  }}
                >
                  Description <span style={{ fontWeight: 400, color: 'var(--gray-color)' }}>(optional)</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                  placeholder={
                    type === 'bug'
                      ? 'What happened? What did you expect to happen?'
                      : 'Share your thoughts or suggestions...'
                  }
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--input-border, #599BE3)',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                  }}
                />
              </div>

              {error && (
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    fontSize: '0.85rem',
                  }}
                >
                  {error}
                </div>
              )}

              {/* Footer buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button
                  type="button"
                  onClick={handleClose}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    backgroundColor: '#fff',
                    color: 'var(--dark-color, #212529)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    border: 'none',
                    backgroundColor: 'var(--primary-color, #072040)',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontSize: '0.9rem',
                    opacity: submitting ? 0.7 : 1,
                  }}
                >
                  {submitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default FeedbackModal;
